<template>
    <section
            :class="classes"
            :id="`endpoint-${endpoint.slug}`"
    >
        <header class="endpoint-header">
            <h3 class="endpoint-title">{{ endpointTitle }}</h3>
        </header>
        <div class="endpoint-body">
            <div class="endpoint-body-inner">
                <endpoint-description :description="endpoint.description" class="endpoint-description"/>
                <endpoint-http-call :methods="endpoint.methods" :uri="endpoint.uri"/>
                <endpoint-parameters :endpoint-id="endpoint.id"
                                     :parameters="endpoint.parameters"
                                     :values="parameterValues"
                                     @blob="fileParameterValue = $event"
                />

                <footer class="endpoint-footer">
                    <div :class="`code-example-tabs language-${codeSampleLanguage}`">
                        <text-button class="tab tab-shell"
                                     label="CURL"
                                     subtle
                                     @click="setCodeSampleLanguage('shell')"/>
                        <text-button class="tab tab-javascript"
                                     label="JS"
                                     subtle
                                     @click="setCodeSampleLanguage('javascript')"/>
                        <text-button class="tab tab-php"
                                     label="PHP"
                                     subtle
                                     @click="setCodeSampleLanguage('php')"/>
                        <text-button class="tab tab-python"
                                     label="Python"
                                     subtle
                                     @click="setCodeSampleLanguage('python')"/>
                    </div>
                    <text-button :disabled="!inputComplete"
                                 label="Submit"
                                 primary
                                 @keyup.self.enter="submit"
                                 @keyup.self.space="submit"
                                 @click="submit"
                    />
                </footer>
                <div class="api-request-response">
                    <code-block v-if="requestStarted"
                                language="http"
                                :code="requestString"
                    />
                    <code-block v-if="responseAvailable"
                                language="http"
                                :code="responseString"
                    />
                </div>
                <code-sample v-show="codeSampleLanguage"
                             :language="codeSampleLanguage"
                             :endpoint="endpoint"
                             :parameters="typedParameterValues"
                             :indentation="4"
                />
            </div>
            <aside class="endpoint-sidebar">
                <div class="endpoint-sidebar-inner">
                    <endpoint-warning v-if="endpoint.deprecated"
                                      type="deprecated"
                                      :message="endpoint.deprecationNotice || 'This endpoint is deprecated and won\'t be supported in a future version'"
                                      class="endpoint-sidebar-section"/>
                    <endpoint-warning v-if="endpoint.internal"
                                      type="internal"
                                      message="This endpoint is not intended for public usage and might be changed or removed at any time."
                                      class="endpoint-sidebar-section"/>
                    <endpoint-errors :errors="endpoint.errors" class="endpoint-sidebar-section"/>
                    <endpoint-scopes :scopes="endpoint.scopes" class="endpoint-sidebar-section"/>
                </div>
            </aside>
        </div>
    </section>
</template>

<!--suppress JSValidateTypes -->
<script>
  import Markdown            from 'vue-markdown-v2';
  import { getStatusText }   from '../modules/HttpStatusCode';
  import CodeBlock           from './CodeBlock';
  import CodeSample          from './CodeSample';
  import EndpointDescription from './Endpoint/EndpointDescription';
  import EndpointErrors      from './Endpoint/EndpointErrors';
  import EndpointHttpCall    from './Endpoint/EndpointHttpCall';
  import EndpointParameters  from './Endpoint/EndpointParameters';
  import EndpointScopes      from './Endpoint/EndpointScopes';
  import EndpointWarning     from './Endpoint/EndpointWarning';
  import BaseField           from './Forms/BaseField';
  import CheckboxField       from './Forms/CheckboxField';
  import IconButton          from './Forms/IconButton';
  import NumberField         from './Forms/NumberField';
  import TextButton          from './Forms/TextButton';
  import TextField           from './Forms/TextField';
  import Icon                from './Layout/Icon';

  export default {
    name: 'ApiEndpoint',

    components: {
      CodeBlock,
      EndpointScopes,
      EndpointParameters,
      EndpointErrors,
      EndpointWarning,
      EndpointHttpCall,
      EndpointDescription,
      CodeSample,
      TextButton,
      CheckboxField,
      NumberField,
      TextField,
      BaseField,
      IconButton,
      Icon,
      Markdown,
    },

    props: {
      /**
       * @var {Endpoint} endpoint
       */
      endpoint: {
        type:     Object,
        required: true,
      },
    },

    data () {
      return {
        codeSampleLanguage: null,
        parameterValues:    {},

        /**
         * @type {File}
         */
        fileParameterValue: null,
        requestStarted:     false,
        responseAvailable:  false,
        requestString:      null,
        responseString:     null,
      };
    },

    mounted () {
      // Set the initial parameter value for each field after mount. We'll either use the
      // default value or just null if none available
      this.endpoint.parameters.forEach(parameter => {
        let defaultValue = parameter.defaultValue;

        if ( defaultValue === 'true' || defaultValue === 'false' ) {
          defaultValue = Boolean(defaultValue);
        } else if ( defaultValue !== null && !isNaN(defaultValue) ) {
          defaultValue = Number(defaultValue);
        }

        this.$set(this.parameterValues, parameter.name, defaultValue);
      });
    },

    computed: {
      classes () {
        return {
          'api-element':          true,
          'api-element-endpoint': true,
        };
      },

      endpointTitle () {
        const title = this.endpoint.name
          .replace(/([^A-Z]*)([A-Z]*)([A-Z])([^A-Z]*)/g, '$1 $2 $3$4')
          .replace(/ +/g, ' ')
          .trim();

        return title.charAt(0).toUpperCase() + title.slice(1);
      },

      inputComplete () {
        if ( !this.$auth.isAuthorized() ) {
          return false;
        }

        for ( const parameter of this.endpoint.parameters ) {
          if ( parameter.optional ) {
            continue;
          }

          if (
            ( this.parameterValues[ parameter.name ] === null &&
              !parameter.types.includes('null')
            ) || (
              this.parameterValues[ parameter.name ] === ''
            )
          ) {
            return false;
          }
        }

        return true;
      },

      typedParameterValues () {
        return Object.entries(this.parameterValues).reduce(( values, [ parameterName, value ] ) => {
          const parameter = this.endpoint.parameters.find(parameter => parameter.name === parameterName);

          if (
            parameter
            && parameter.types[ 0 ]
            && ( parameter.types[ 0 ].name === 'object' || parameter.types[ 0 ].name === 'array' )
          ) {
            let parsed;

            try {
              parsed = JSON.parse(value);
            } catch ( error ) {
              parsed = null;
            }

            values[ parameterName ] = parsed;

            return values;
          }

          values[ parameterName ] = value;

          return values;
        }, {});
      },
    },

    methods: {
      setCodeSampleLanguage ( language ) {
        this.requestStarted     = false;
        this.responseAvailable  = false;
        this.requestString      = null;
        this.responseString     = null;
        this.codeSampleLanguage = this.codeSampleLanguage === language
          ? null
          : language;
      },

      /**
       * Executes the API request
       *
       * @returns {Promise<void>}
       */
      async submit () {
        this.responseAvailable  = false;
        this.codeSampleLanguage = null;

        const uri = this.endpoint.uriArguments.reduce(( uri, arg ) => {
          const value = this.parameterValues[ arg.name ] || null;

          return uri.replace(`{${ arg.name }}`, value);
        }, this.endpoint.uri);

        const url  = new URL(uri, window.location.origin);
        const data = {
          method:  this.endpoint.methods[ 0 ],
          headers: {
            Accept:        'application/vnd.messengerpeople.v1+json',
            Authorization: `Bearer ${ this.$auth.token }`,
          },
        };

        this.endpoint.queryParameters.forEach(param => {
          const value = this.parameterValues[ param.name ] || null;

          url.append(param.name, value);
        });

        if ( [ 'POST', 'PUT', 'PATCH' ].includes(this.endpoint.methods[ 0 ]) ) {
          if ( this.endpoint.upload ) {
            const body                       = this.fileParameterValue;
            data.headers[ 'Content-Type' ]   = body.type;
            data.headers[ 'Content-Length' ] = body.size;
            data.body                        = body;
          } else {
            const body = this.endpoint.bodyParameters.reduce(( params, param ) => {
              let bodyParameterValue = this.parameterValues[ param.name ] || null;

              if ( param.types
                && param.types[ 0 ]
                && ( param.types[ 0 ].name === 'array' || param.types[ 0 ].name === 'object' )
              ) {
                bodyParameterValue = JSON.parse(bodyParameterValue);
              }

              params[ param.name ] = bodyParameterValue;

              return params;
            }, {});

            if ( Object.keys(body).length > 0 ) {
              data.headers[ 'Content-Type' ] = 'application/vnd.messengerpeople.v1+json';
              data.body                      = JSON.stringify(body, null, 4);
            }
          }
        }

        this.buildRequestString(url, data.body);
        this.requestStarted = true;

        let response;

        try {
          response = await fetch(url.toString(), data);
        } catch ( error ) {
          console.error('Error:', error);
        }

        await this.buildResponseString(response);
        this.responseAvailable = true;
        console.info('Response', response);
      },

      /**
       * Builds the response string
       *
       * @param  {Response} response
       */
      async buildResponseString ( response ) {
        const contentType = response.headers.get('content-type');
        let body;

        if ( contentType.includes('json') || contentType.includes('text') ) {
          const bodyText = await response.text();

          try {
            body = JSON.stringify(JSON.parse(bodyText), null, 4);
          } catch ( error ) {
            body = bodyText;
          }
        } else {
          body = '<BINARY DATA>';
        }

        const headers = Array.from(response.headers.entries())
          .map(( [ name, value ] ) => [
            name
              .split('-')
              .map(text => text.charAt(0).toUpperCase() + text.substr(1).toLowerCase())
              .join('-'),
            value,
          ])
          .map(( [ name, value ] ) => `${ name }: ${ value }`);

        const statusText = response.statusText || getStatusText(response.status);

        this.responseString = [
          `HTTP/1.1 ${ response.status } ${ statusText }`,
          ...headers,
          '',
          body,
        ].join('\n');
      },

      /**
       * @param {URL} url
       * @param {string|File} body
       */
      buildRequestString ( url, body ) {
        const httpMethod = this.endpoint.methods[ 0 ].toUpperCase();
        const host       = window.location.hostname;
        const port       = window.location.port;

        const hostHeader = host + ( [ 80, 443 ].includes(port) ? '' : ':' + port );

        const request = [
          `${ httpMethod } ${ url.pathname } HTTP/1.1`,
          `Host: ${ hostHeader }`,
          `User-Agent: ${ window.navigator.userAgent }`,
          `Authorization: Bearer ******`,
          'Accept: application/json',
        ];

        if ( [ 'POST', 'PUT', 'PATCH' ].includes(httpMethod) ) {
          if ( this.endpoint.upload ) {
            request.push('Content-Type: ' + body.type);
            request.push('Content-Length: ' + body.size);
            request.push('');
            request.push('<BINARY DATA>');
          } else {
            request.push('Content-Type: application/vnd.messengerpeople.v1+json');
            request.push('');
            request.push(body);
          }
        }

        this.requestString = request.join('\n');
      },
    },
  };

</script>

<!--suppress CssFloatPxLength -->
<style scoped>
    .api-element-endpoint {
        padding:       1rem 0;
        border-radius: 4px;
    }

    .api-element-endpoint + .api-element-endpoint {
        margin-top:  2rem;
        padding-top: 3rem;
        border-top:  1px solid var(--color-gray-light);
    }

    .endpoint-header {
        display:         flex;
        justify-content: flex-start;
        align-items:     center;
        cursor:          pointer;
    }

    .endpoint-title {
        margin:      0;
        font-size:   1.6rem;
        font-weight: 500;
        color:       var(--color-primary);
    }

    .endpoint-body {
        display:        flex;
        flex-direction: column;
    }

    .endpoint-sidebar {
        flex:       0 0 30%;
        margin-top: 1rem;
    }

    .endpoint-section {
        margin-top: 2rem;
    }

    .endpoint-sidebar-section {
        margin-top:    0;
        padding:       0.5rem;
        border:        2px solid var(--color-primary-highlight);
        border-radius: 5px;
        background:    var(--color-white);
    }

    .endpoint-sidebar-section:not(:first-child) {
        margin-top: 1rem;
    }

    .endpoint-sidebar-inner {
        position: sticky;
        top:      6rem;
    }

    .endpoint-description {
        margin-top: 1rem;
    }

    .data-missing {
        color: var(--color-gray-medium);
    }

    .endpoint-footer {
        display:         flex;
        justify-content: space-between;
        align-items:     center;
        margin-top:      1rem;
        padding-top:     1rem;
    }

    .endpoint-footer .code-example-tabs {
        display:         flex;
        justify-content: center;
        align-items:     center;
    }

    .tab {
        padding:       0.5rem 1rem;
        border-radius: 3px;
        background:    rgba(0, 0, 0, 0.05);
    }

    .code-example-tabs.language-shell .tab-shell,
    .code-example-tabs.language-javascript .tab-javascript,
    .code-example-tabs.language-php .tab-php,
    .code-example-tabs.language-python .tab-python {
        box-shadow:    inset 0 -4px 0 -2px var(--color-primary);
        color:         var(--color-primary);
        border-radius: 3px 3px 0 0;
    }

    .tab + .tab {
        margin-left: 0.5rem;
    }

    .code-sample {
        padding-top: 1rem;
    }

    @media screen and (min-width: 1280px) {
        .endpoint-body {
            flex-direction: row;
        }

        .endpoint-sidebar {
            margin-top: 0;
        }

        .endpoint-body-inner {
            flex:          0 0 70%;
            padding-right: 2rem;
        }
    }
</style>
